import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { capitalize } from 'es-toolkit';
import { useAtom } from 'jotai';
import { ArrowUp, Loader, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AskMode } from '@/entities/ask-syncly';
import { AutoResizeTextarea } from '@/shared/components/auto-resize-textarea';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { cn } from '@/shared/utils/cn';
import { ConstMap } from '@/shared/utils/const-map';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { askSynclyFilterAtom } from '../atoms/ask-syncly-filter-atom';
import { ModeSelector } from './chat-input.mode-selector';

// Form validation schema
const messageSchema = z.object({
  message: z
    .string()
    .min(1, 'Message cannot be empty')
    .max(1000, 'Message must be less than 1000 characters')
    .refine((value) => value.trim().length > 0, 'Message cannot be only whitespace'),
});

type MessageFormData = z.infer<typeof messageSchema>;

interface ChatInputProps {
  className?: string;
  onSubmit: (message: string) => void;
  loading?: boolean;
  hasPreviousAnswer: boolean;
  mode: AskMode;
  onModeChange: (mode: AskMode) => void;
}

const placeholderMap = new ConstMap([
  [AskMode.GENERAL, 'Ask a research question — this may take a minute.'],
  [AskMode.CONTENT, 'Ask about video hooks or selling points'],
]);

export function ChatInput({
  className,
  onSubmit,
  loading,
  hasPreviousAnswer,
  mode,
  onModeChange,
}: ChatInputProps) {
  const [askSynclyFilter, setAskSynclyFilter] = useAtom(askSynclyFilterAtom);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<MessageFormData>({
    resolver: zodResolver(messageSchema),
    mode: 'onChange',
    defaultValues: {
      message: '',
    },
  });

  const messageValue = watch('message');
  const isFormDisabled = loading;

  const onSubmitForm = (data: MessageFormData) => {
    if (!isFormDisabled && isValid) {
      onSubmit(data.message.trim());
      reset();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't submit when IME is composing (for Korean, Chinese, Japanese input)
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSubmit(onSubmitForm)();
    }
  };
  const { query: selectedQuery } = useSelectedSavedQuery();

  return (
    <div className={cn('flex flex-col gap-2 bg-zinc-100 p-5 pt-0', className)}>
      <ModeSelector value={mode} onChange={onModeChange} />
      <div className="rounded-md border border-zinc-200 bg-white">
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-3 px-4 py-3">
          <div className="flex items-center gap-1">
            <Badge variant="secondary">{selectedQuery.name}</Badge>
            {askSynclyFilter?.dateRange && (
              <Badge
                variant="secondary"
                className="border-primary flex items-center gap-1 bg-transparent"
              >
                {dayjs(askSynclyFilter.dateRange.startDate).format(DATE_FORMATS.base)} ~{' '}
                {dayjs(askSynclyFilter.dateRange.endDate).format(DATE_FORMATS.base)}
                <X className="size-4 cursor-pointer" onClick={() => setAskSynclyFilter(null)} />
              </Badge>
            )}
            {askSynclyFilter?.filter &&
              Object.entries(askSynclyFilter.filter).map(([key, value]) => (
                <Badge
                  variant="secondary"
                  key={key}
                  className="border-primary flex items-center gap-1 bg-transparent"
                >
                  {capitalize(key)}: {value}
                </Badge>
              ))}
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-1">
              <AutoResizeTextarea
                {...register('message')}
                onKeyDown={handleKeyDown}
                placeholder={hasPreviousAnswer ? 'Ask follow-up' : placeholderMap.get(mode)}
                disabled={isFormDisabled}
                className="border-0 bg-transparent p-0 text-base text-zinc-900 shadow-none focus-visible:ring-0"
                maxHeight={120}
                minHeight={40}
              />
            </div>
            <Button
              className="self-end"
              type="submit"
              size="icon"
              disabled={!messageValue?.trim() || !isValid || isFormDisabled}
            >
              {loading ? (
                <Loader className="size-4 animate-spin" />
              ) : (
                <ArrowUp className="size-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
